<template>
  <div class="flex-grow-1 d-flex flex-column bg-grey-lighten-4 overflow-hidden">
    <template v-if="selectedCustomer">
      <!-- 1. Encabezado del Cliente Seleccionado -->
      <div class="pa-3 bg-white border-bottom flex-grow-0">
        <div class="d-flex align-center justify-space-between flex-wrap gap-2 mb-2">
          <div class="d-flex align-center">
            <v-avatar color="primary lighten-5" size="36" class="mr-2">
              <v-icon small color="primary">mdi-domain</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle-2 font-weight-black grey--text text--darken-4 mb-0 d-flex align-center">
                {{ selectedCustomer.customerName }}
                <v-chip v-if="isNoClient" x-small color="indigo lighten-5" text-color="indigo darken-4" class="ml-2 font-weight-bold">
                  Reserva Sin Asignar
                </v-chip>
                <v-chip v-else-if="selectedCustomer.statusKey === 'bag_allocated'" x-small color="purple lighten-5" text-color="deep-purple darken-3" class="ml-2 font-weight-bold">
                  <v-icon x-small left color="deep-purple darken-3">mdi-tray-arrow-down</v-icon>
                  Asignado desde bolsa
                </v-chip>
              </div>
              <span class="caption grey--text text--darken-2">
                {{ isNoClient ? 'Volumen general presupuestado disponible para asignar a clientes destino' : `Código SAP: ${selectedCustomer.customerId || 'Sin código'}` }} | {{ clientLines.length }} líneas registradas
              </span>
            </div>
          </div>

          <!-- Botones de Acción para el Cliente -->
          <div v-if="!isView" class="d-flex align-center gap-2">
            <v-btn
              color="primary"
              small
              class="text-capitalize rounded-lg font-weight-bold"
              @click="$emit('add-line')"
            >
              <v-icon left small>mdi-plus</v-icon>
              {{ isNoClient ? 'Asignar a Cliente' : 'Nueva Línea' }}
            </v-btn>

            <v-btn
              v-if="clientLines.length > 0"
              color="teal darken-2"
              outlined
              small
              class="text-capitalize rounded-lg font-weight-bold"
              @click="$emit('add-similar', clientLines[clientLines.length - 1])"
            >
              <v-icon left small>mdi-content-duplicate</v-icon>
              Agregar Similar
            </v-btn>
          </div>
        </div>

        <!-- 3 Mini Cards Comparativas del Cliente (TM, FCL, FOB) -->
        <v-row dense class="mt-1">
          <!-- TM -->
          <v-col cols="12" sm="4">
            <div class="client-kpi-card pa-2 rounded-lg border-light bg-white">
              <div class="caption font-weight-bold grey--text text--darken-2 d-flex justify-space-between align-center">
                <span>VOLUMEN TONELADAS (TM)</span>
                <span v-if="selectedCustomer.allocatedFromBagTons > 0" class="deep-purple--text text--darken-3" style="font-size: 10px;">
                  +{{ formatNumber(selectedCustomer.allocatedFromBagTons, 2) }} TM Bolsa
                </span>
              </div>
              <div class="d-flex justify-space-between align-center mt-1 text-caption flex-wrap gap-1">
                <span>Ppto: <strong>{{ formatNumber(selectedCustomer.directBudgetTons !== undefined ? selectedCustomer.directBudgetTons : selectedCustomer.budgetTons, 2) }}</strong></span>
                <span>Base: <strong>{{ formatNumber(selectedCustomer.availableBaseTons || selectedCustomer.budgetTons, 2) }}</strong></span>
                <span class="primary--text font-weight-bold">Prog: {{ formatNumber(selectedCustomer.programmedTons, 2) }}</span>
                <span class="font-weight-bold" :class="(selectedCustomer.operatingBalanceTons !== undefined ? selectedCustomer.operatingBalanceTons : selectedCustomer.balanceTons) < 0 ? 'red--text' : 'green--text text--darken-3'">
                  Saldo: {{ formatNumber(selectedCustomer.operatingBalanceTons !== undefined ? selectedCustomer.operatingBalanceTons : selectedCustomer.balanceTons, 2) }} TM
                  <span v-if="(selectedCustomer.operatingBalanceTons !== undefined ? selectedCustomer.operatingBalanceTons : selectedCustomer.balanceTons) < 0">⚠️</span>
                </span>
              </div>
            </div>
          </v-col>

          <!-- FCL -->
          <v-col cols="12" sm="4">
            <div class="client-kpi-card pa-2 rounded-lg border-light bg-white">
              <div class="caption font-weight-bold grey--text text--darken-2 d-flex justify-space-between align-center">
                <span>CONTENEDORES (FCL)</span>
                <span v-if="selectedCustomer.allocatedFromBagFcl > 0" class="deep-purple--text text--darken-3" style="font-size: 10px;">
                  +{{ formatNumber(selectedCustomer.allocatedFromBagFcl, 1) }} FCL Bolsa
                </span>
              </div>
              <div class="d-flex justify-space-between align-center mt-1 text-caption flex-wrap gap-1">
                <span>Ppto: <strong>{{ formatNumber(selectedCustomer.directBudgetFcl !== undefined ? selectedCustomer.directBudgetFcl : selectedCustomer.budgetFcl, 1) }}</strong></span>
                <span>Base: <strong>{{ formatNumber(selectedCustomer.availableBaseFcl || selectedCustomer.budgetFcl, 1) }}</strong></span>
                <span class="font-weight-bold">Prog: {{ formatNumber(selectedCustomer.programmedFcl, 1) }}</span>
                <span class="font-weight-bold" :class="(selectedCustomer.operatingBalanceFcl !== undefined ? selectedCustomer.operatingBalanceFcl : selectedCustomer.balanceFcl) < 0 ? 'red--text' : 'green--text text--darken-3'">
                  Saldo: {{ formatNumber(selectedCustomer.operatingBalanceFcl !== undefined ? selectedCustomer.operatingBalanceFcl : selectedCustomer.balanceFcl, 1) }} FCL
                </span>
              </div>
            </div>
          </v-col>

          <!-- FOB USD -->
          <v-col cols="12" sm="4">
            <div class="client-kpi-card pa-2 rounded-lg border-light bg-white">
              <div class="caption font-weight-bold grey--text text--darken-2 d-flex justify-space-between align-center">
                <span>IMPORTE FOB EQUIVALENTE</span>
                <span v-if="selectedCustomer.allocatedFromBagFobUsd > 0" class="deep-purple--text text--darken-3" style="font-size: 10px;">
                  +${{ formatNumber(selectedCustomer.allocatedFromBagFobUsd, 2) }} Bolsa
                </span>
              </div>
              <div class="d-flex justify-space-between align-center mt-1 text-caption flex-wrap gap-1">
                <span>Ppto: <strong>${{ formatNumber(selectedCustomer.directBudgetFobUsd !== undefined ? selectedCustomer.directBudgetFobUsd : selectedCustomer.budgetFobUsd, 2) }}</strong></span>
                <span class="green--text text--darken-4 font-weight-bold">Prog: ${{ formatNumber(selectedCustomer.programmedFobUsd, 2) }}</span>
                <span class="font-weight-bold" :class="(selectedCustomer.operatingBalanceFobUsd !== undefined ? selectedCustomer.operatingBalanceFobUsd : selectedCustomer.balanceFobUsd) < 0 ? 'red--text' : 'green--text text--darken-3'">
                  Saldo: ${{ formatNumber(selectedCustomer.operatingBalanceFobUsd !== undefined ? selectedCustomer.operatingBalanceFobUsd : selectedCustomer.balanceFobUsd, 2) }}
                </span>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- 2. Lista de Líneas Comerciales o Estado Vacío -->
      <div class="flex-grow-1 overflow-y-auto pa-3">
        <!-- Estado Vacío: Cliente sin líneas -->
        <div v-if="clientLines.length === 0" class="text-center py-10 grey--text">
          <v-icon size="56" color="grey lighten-2" class="d-block mb-2">mdi-package-variant-closed-plus</v-icon>
          <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">
            Este cliente todavía no tiene líneas comerciales
          </div>
          <div class="caption grey--text text--darken-1 mb-4">
            Registra el primer producto para comenzar a distribuir su presupuesto.
          </div>
          <v-btn
            v-if="!isView"
            color="primary"
            small
            class="rounded-lg text-capitalize px-4 font-weight-bold"
            @click="$emit('add-line')"
          >
            <v-icon left small>mdi-plus</v-icon>
            Agregar primera línea
          </v-btn>
        </div>

        <!-- Tarjetas Compactas de Líneas -->
        <div v-else class="d-flex flex-column gap-2">
          <commercial-line-card
            v-for="(line, index) in clientLines"
            :key="line._tempId || line.EpdID || index"
            :line="line"
            :index="index"
            :is-view="isView"
            @edit="$emit('edit-line', $event)"
            @duplicate="$emit('duplicate-line', $event)"
            @add-similar="$emit('add-similar', $event)"
            @delete="$emit('delete-line', $event)"
          />
        </div>
      </div>
    </template>

    <!-- Estado Vacío: Ningún cliente seleccionado -->
    <div v-else class="flex-grow-1 d-flex align-center justify-center text-center pa-6 grey--text">
      <div>
        <v-icon size="64" color="grey lighten-2">mdi-account-arrow-left-outline</v-icon>
        <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mt-2">
          Seleccione un cliente del panel izquierdo
        </div>
        <div class="caption grey--text text--darken-1">
          Podrá gestionar sus líneas comerciales de forma clara sin tablas horizontales excesivas.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CommercialLineCard from "./CommercialLineCard.vue";
import { formatNumber } from "../utils/CommercialProgrammingCalculations";

export default {
  name: "CommercialClientDetail",
  components: {
    CommercialLineCard,
  },
  props: {
    selectedCustomer: {
      type: Object,
      default: null,
    },
    clientLines: {
      type: Array,
      default: () => [],
    },
    isView: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isNoClient() {
      return Boolean(
        this.selectedCustomer &&
          (!this.selectedCustomer.customerId ||
            this.selectedCustomer.customerName === "Sin Cliente")
      );
    },
  },
  methods: {
    formatNumber,
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.gap-2 {
  gap: 8px;
}
.client-kpi-card {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}
</style>
